// "RUNNING IDW INTERPOLATION" — Map-With-Existing → Classic Grid: ripples expanding from the SAMPLE
// LOCATIONS, looping until the window closes.
import { polygonsOf } from "./samplingDesign";

export interface RippleLayout {
  /* * SVG viewBox width/height (the field's own aspect, letterboxed inside 400 units) */
  w: number;
  h: number;
  /* * the boundary rings in viewBox space (empty when no boundary was available) */
  rings: [number, number][][];
  /* * the sample locations in viewBox space, with a stagger delay (seconds) per point */
  points: { x: number; y: number; delay: number }[];
  /* * ripple radius in viewBox units — a share of the field, so a dense grid still reads as ripples */
  radius: number;
}

const BOX = 400;
const PAD = 12;
/* * one ripple: grow + fade over this many seconds, then again from zero — forever */
export const RIPPLE_S = 2.4;
/* * how far a ripple travels: about the typical spacing of a 2.5-ac grid on a small field */
const RADIUS_SHARE = 0.16;
/* * the ripple wave rolls across the field: points start by their position, not all at once */
const STAGGER_S = 1.2;

/* * the sample locations of a POINTS layer, [lng, lat] — anything that is not a Point is ignored */
export function pointCoords(
  fc: GeoJSON.FeatureCollection | null | undefined,
): [number, number][] {
  const out: [number, number][] = [];
  for (const f of fc?.features ?? []) {
    const g = f.geometry;
    if (
      g?.type === "Point" &&
      Array.isArray(g.coordinates) &&
      Number.isFinite(g.coordinates[0]) &&
      Number.isFinite(g.coordinates[1])
    ) {
      out.push([g.coordinates[0], g.coordinates[1]]);
    }
  }
  return out;
}

export function rippleLayout(
  points: [number, number][],
  boundary?:
    GeoJSON.Geometry | GeoJSON.Feature | GeoJSON.FeatureCollection | null,
): RippleLayout | null {
  const rings = boundary
    ? polygonsOf(boundary).flatMap((polys) => polys.slice(0, 1))
    : [];
  const all: [number, number][] = [...points, ...rings.flat()];
  if (!all.length) return null;
  let w0 = Infinity,
    s0 = Infinity,
    e0 = -Infinity,
    n0 = -Infinity;
  for (const [x, y] of all) {
    if (x < w0) w0 = x;
    if (x > e0) e0 = x;
    if (y < s0) s0 = y;
    if (y > n0) n0 = y;
  }
  const cosLat = Math.cos((((s0 + n0) / 2) * Math.PI) / 180) || 1;
  const lonW = Math.max(1e-9, (e0 - w0) * cosLat);
  const latH = Math.max(1e-9, n0 - s0);
  const aspect = lonW / latH;
  const w = aspect >= 1 ? BOX : Math.max(120, Math.round(BOX * aspect));
  const h = aspect >= 1 ? Math.max(120, Math.round(BOX / aspect)) : BOX;
  const sx = (w - 2 * PAD) / lonW;
  const sy = (h - 2 * PAD) / latH;
  const px = (lng: number) => PAD + (lng - w0) * cosLat * sx;
  const py = (lat: number) => h - PAD - (lat - s0) * sy;
  const proj = points.map(([lng, lat]) => ({ x: px(lng), y: py(lat) }));
  // stagger by position along the field's diagonal, so the ripples roll across rather than pulse in unison
  const diag = Math.max(1, w + h);
  return {
    w,
    h,
    rings: rings.map((r) =>
      r.map(([lng, lat]) => [px(lng), py(lat)] as [number, number]),
    ),
    points: proj.map((p) => ({
      ...p,
      delay: Math.round(((p.x + p.y) / diag) * STAGGER_S * 100) / 100,
    })),
    radius: Math.round(Math.min(w, h) * RADIUS_SHARE),
  };
}
